import React from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { Circle, ThumbsUp } from 'lucide-react';
import { createWigglePreset } from '../utils/progressAnimationUtils';
import { useAnimationValue } from '../hooks/useAnimationValue';

const OptionTwirly = ({ val, className = '' }) => (
  <div className={`w-full h-full object-cover aspect-square rounded-lg p-4 flex items-center justify-center ${className}`}>
    <p className="text-md text-gray-800 font-bold text-center font-mono">{val}</p>
  </div>
);

// Timings rescaled by 5/6 for the 7-card layout (ProcureWin inserted after Twirly).
// Twirly's card center moved from progress 20 → 16.67.
const TWIRLY_PREV_ANIM = [
  { initialValue: 220, startTiming: 7.5 },
  { initialValue: 0, finalValue: -320, startTiming: 16.67, duration: 13.33 },
];
const TWIRLY_IMAGE1_ANIM = [
  { initialValue: 220, startTiming: 8.33 },
  { initialValue: 0, finalValue: -320, startTiming: 16.67, duration: 13.33 },
];
const TWIRLY_IMAGE2_ANIM = [
  { initialValue: 220, startTiming: 8.75 },
  { initialValue: 0, finalValue: -320, startTiming: 16.67, duration: 13.33 },
];
const TWIRLY_IMAGE3_ANIM = [
  { initialValue: 220, startTiming: 9.17 },
  { initialValue: 0, finalValue: -320, startTiming: 16.67, duration: 13.33 },
];
const TWIRLY_IMAGE4_SLIDE_ANIM = [
  { initialValue: 220, startTiming: 9.58 },
  { initialValue: 0, finalValue: -320, startTiming: 16.67, duration: 13.33 },
];
const TWIRLY_IMAGE4_SCALE_ANIM = [{ initialValue: 1, finalValue: 1.1, startTiming: 15, duration: 1.67 }];
const TWIRLY_5_THUMBSUP_ANIM = [{ initialValue: 0, finalValue: 1, startTiming: 20.83, duration: 0.83 }];
const TITLE_ANIM = [{ initialValue: 100, startTiming: 5.83, duration: 1.67 }];
const DESCRIPTION_ANIM = [{ initialValue: 70, startTiming: 6.67, duration: 2.5 }];
const COMMON_FADE_ANIM = [{ initialValue: 1, finalValue: 0.95, startTiming: 15, duration: 4.17 }];
const THUMBSUP_FADE_ANIM = [{ initialValue: 0, finalValue: 1, startTiming: 15.83, duration: 0.83 }];
const THUMBSUP_WIGGLE_ROTATE_ANIM = createWigglePreset(13.33, 'rotate', false);
const THUMBSUP_WIGGLE_SCALE_ANIM = createWigglePreset(13.33, 'scale', false);

export const TwirlyProject = ({ progressMotionValue, isMobile = false }) => {
  const fallbackProgress = useMotionValue(0);
  // On mobile, freeze at a moment where the phone screen has all cards
  // settled in view (slide-ins done at ~12.5, slide-outs start at 16.67 in the 7-card layout).
  const mobileStaticProgress = useMotionValue(14.17);
  const activeProgress = isMobile
    ? mobileStaticProgress
    : (progressMotionValue ?? fallbackProgress);
  const titleSlideX = useAnimationValue(activeProgress, TITLE_ANIM, 'slideX', 0);
  const descriptionSlideX = useAnimationValue(activeProgress, DESCRIPTION_ANIM, 'slideX', 0);
  const twirlyPrevSlideY = useAnimationValue(activeProgress, TWIRLY_PREV_ANIM, 'slideY', 0);
  const twirlyImage1SlideY = useAnimationValue(activeProgress, TWIRLY_IMAGE1_ANIM, 'slideY', 0);
  const twirlyImage2SlideY = useAnimationValue(activeProgress, TWIRLY_IMAGE2_ANIM, 'slideY', 0);
  const twirlyImage3SlideY = useAnimationValue(activeProgress, TWIRLY_IMAGE3_ANIM, 'slideY', 0);
  const twirlyImage4SlideY = useAnimationValue(activeProgress, TWIRLY_IMAGE4_SLIDE_ANIM, 'slideY', 0);
  const twirlyImage4Scale = useAnimationValue(activeProgress, TWIRLY_IMAGE4_SCALE_ANIM, 'scale', 1);
  const commonFade = useAnimationValue(activeProgress, COMMON_FADE_ANIM, 'fade', 1);
  const appearThumbsUp = useAnimationValue(activeProgress, THUMBSUP_FADE_ANIM, 'fade', 1);
  const appearThumbsUp5 = useAnimationValue(activeProgress, TWIRLY_5_THUMBSUP_ANIM, 'fade', 0);
  const wiggleRotation = useAnimationValue(activeProgress, THUMBSUP_WIGGLE_ROTATE_ANIM, 'rotate', 0);
  const wiggleScale = useAnimationValue(activeProgress, THUMBSUP_WIGGLE_SCALE_ANIM, 'scale', 1, (value) => value * 1.5);

  const twirlyImage4BackgroundColor = useTransform(appearThumbsUp, [0, 1], ['#e5e7eb', 'rgb(255, 237, 166)']);
  const twirlyImage5BackgroundColor = useTransform(appearThumbsUp5, [0, 1], ['#e5e7eb', 'rgb(166, 194, 255)']);

  return (
    <div className=" md:w-screen sm:w-full h-screen md:h-screen px-4 sm:px-6 py-10 md:py-0 flex items-center justify-center relative flex-shrink-0">
      <div className="absolute inset-0 bg-gradient-to-b from-gray-900 to-purple-900"></div>
      <div className="absolute inset-0 bg-gradient-to-l from-purple-900 to-transparent "></div>

      <div className="relative z-10 grid grid-cols-2 gap-8 gap-12 items-center max-w-6xl">
        <div className="w-screen sm:w-full md:w-full">
          <motion.h3 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 text-cyan-400" style={{ x: titleSlideX }}>
            Twirly App
          </motion.h3>
          <motion.p className="text-base sm:text-lg md:text-xl text-white mb-6" style={{ x: descriptionSlideX }}>
            Pick a side on anything. A live head-to-head real-time <motion.span className=" text-cyan-400">social comparison platform</motion.span> with real votes, real arguments, and a native mobile app.
          </motion.p>
          <div className="relative flex flex-wrap gap-3 mb-6">
            <span className="px-3 py-1 bg-blue-600 rounded-full text-sm">React</span>
            <span className="px-3 py-1 bg-green-600 rounded-full text-sm">Supabase</span>
            <span className="px-3 py-1 bg-purple-600 rounded-full text-sm">PostgreSQL</span>
            <span className="px-3 py-1 bg-cyan-600 rounded-full text-sm">Tailwind</span>
            <span className="px-3 py-1 bg-pink-600 rounded-full text-sm">Framer Motion</span>
          </div>
          <a href="https://twirlyapp.com" target="_blank" className="text-cyan-400 hover:text-cyan-300 font-semibold" rel="noreferrer">
            View Live Site →
          </a>
        </div>
        <div className="flex justify-center w-screen sm:w-full md:w-full">
          <div className="relative w-[280px] sm:w-[340px] md:w-[400px] h-[560px] sm:h-[680px] md:h-[800px] rounded-[2rem] sm:rounded-[2.6rem] md:rounded-[3rem] border-4 sm:border-6 md:border-8 border-slate-800 bg-slate-950 shadow-2xl shadow-black/50 p-2 sm:p-3 rotate-1">
            <div className="pointer-events-none absolute top-3 left-1/2 z-30 h-7 w-28 -translate-x-1/2 rounded-full bg-slate-900"></div>
            <div className="absolute inset-3 rounded-[2.4rem] overflow-hidden bg-slate-100">
              <motion.div className="absolute inset-0 p-4 " style={{ y: -180 }} >
                <motion.div className="rounded-lg bg-white p-4 mt-4" style={{
                    y: twirlyPrevSlideY,
                  }}
                >
                  <div className="w-full mt-4 mb-4">
                    <p className="inline-flex text-sm text-gray-700">
                      <Circle className="w-3 h-3 mt-1 mr-1" fill="currentColor" /> Dream House Location:
                    </p>
                  </div>

                  <div className="w-full h-full grid grid-cols-2 gap-3">
                    <div className="overflow-hidden rounded-lg">
                      <OptionTwirly val="In the city" className="bg-[#e5e7eb]" />
                    </div>
                    <div className="overflow-hidden rounded-lg">
                      <OptionTwirly val="At the beach" className="bg-[#e5e7eb]" />
                    </div>
                    <motion.div className="relative overflow-hidden rounded-lg" style={{ scale: 1.1 }}>
                      <OptionTwirly val="In the mountains" className="bg-green-100" />
                      <motion.div className="absolute top-0 left-0" style={{ x: 10, y: 8, scale: 1.1 }}>
                        <ThumbsUp className="w-4 h-4 text-green-400" />
                      </motion.div>
                    </motion.div>
                    <div className="relative overflow-hidden rounded-lg">
                      <OptionTwirly val="In the library" className="bg-[#e5e7eb]" />
                    </div>
                  </div>
                  <div className="w-full mt-4 h-1 bg-gray-200"></div>
                </motion.div>

                <motion.div className="rounded-lg bg-white p-4 mt-4" style={{ y: twirlyPrevSlideY }} >
                  <motion.div className="w-full mt-4 mb-4" >
                    <p className="inline-flex text-sm text-gray-700">
                      <Circle className="w-3 h-3 mt-1 mr-1" fill="currentColor" /> I am usually found:
                    </p>
                  </motion.div>

                  <div className=" grid grid-cols-2 gap-3 h-full w-full">
                    <motion.div className="overflow-hidden rounded-lg" style={{ opacity: commonFade }}>
                      <OptionTwirly val="Scheduling meetings" className="bg-[#e5e7eb]" />
                    </motion.div>
                    <motion.div className="overflow-hidden rounded-lg" style={{ opacity: commonFade }}>
                      <OptionTwirly val="Balancing work-life" className="bg-[#e5e7eb]" />
                    </motion.div>
                    <motion.div className="overflow-hidden rounded-lg" style={{ opacity: commonFade }}>
                      <OptionTwirly val="Pivoting strategies" className="bg-[#e5e7eb]" />
                    </motion.div>
                    <motion.div
                      className="relative overflow-hidden rounded-lg"
                      style={{ scale: twirlyImage4Scale, backgroundColor: twirlyImage4BackgroundColor }}
                    >
                      <OptionTwirly val="Shipping features no one asked for" />
                      <motion.div
                        className="absolute top-0 left-0"
                        style={{ scale: wiggleScale, rotate: wiggleRotation, x: 10, y: 8, opacity: appearThumbsUp }}
                      >
                        <ThumbsUp className="w-4 h-4 text-yellow-400" />
                      </motion.div>
                    </motion.div>
                  </div>
                  <motion.div className="w-full mt-4 h-1 bg-gray-200" style={{ opacity: commonFade }}></motion.div>
                </motion.div>

                <motion.div className="rounded-lg bg-white p-4 mt-4"  style={{
                    y: twirlyPrevSlideY,
                  }}
                >
                  <div className="w-full mt-4 mb-4">
                    <p className="inline-flex text-sm text-gray-700">
                      <Circle className="w-3 h-3 mt-1 mr-1" fill="currentColor" /> You should buy me a:
                    </p>
                  </div>

                  <div className="w-full h-full grid grid-cols-2 gap-3">
                    <motion.div className="relative overflow-hidden rounded-lg" style={{ backgroundColor: twirlyImage5BackgroundColor }}>
                      <OptionTwirly val="Vanilla Ice Cream" />
                      <motion.div
                        className="absolute top-0 left-0"
                        style={{ scale: wiggleScale, rotate: wiggleRotation, x: 10, y: 8, opacity: appearThumbsUp5 }}
                      >
                        <ThumbsUp className="w-4 h-4 text-blue-400" />
                      </motion.div>
                    </motion.div>
                    <div className="overflow-hidden rounded-lg">
                      <OptionTwirly val="Chocolate Bar" className="bg-[#e5e7eb]" />
                    </div>
                    <motion.div className="relative overflow-hidden rounded-lg">
                      <OptionTwirly val="Strawberry Smoothie" className="bg-[#e5e7eb]" />
                    </motion.div>
                    <div className="relative overflow-hidden rounded-lg">
                      <OptionTwirly val="Mint Chocolate Chip" className="bg-[#e5e7eb]" />
                    </div>
                  </div>
                  <div className="w-full mt-4 h-1 bg-gray-200"></div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
